/**
 * The guide's own interface text, per guide language. Deliberately not in the
 * app's i18next files: the guide language is chosen in the drawer header and
 * may differ from the app language, so every guide surface reads from here.
 */
import type { HelpLanguage } from "./types";

type Strings = Record<string, string>;

const EN: Strings = {
  "help.launcher": "Help for this page",
  "help.drawer.title": "Page guide",
  "help.drawer.eyebrow": "Page guide",
  "help.drawer.close": "Close page guide",
  "help.drawer.language": "Guide language",
  "help.drawer.tabPage": "This page",
  "help.drawer.tabAsk": "Ask",
  "help.drawer.startTour": "Show me around",
  "help.drawer.inspect": "Point at a field",
  "help.drawer.modesHint":
    'The tour visits what is on screen right now — or only the open dialog, when there is one; its step counter jumps to any of them. "Point at a field" answers the other way round: click the control you are looking at and read what it does.',
  "help.drawer.searchAll": "Search all {{count}} entries…",
  "help.drawer.matches": "{{count}} of {{total}} steps match",
  "help.drawer.tips": "Good to know",
  "help.drawer.floating": "Floating “?” button",
  "help.drawer.floatingAria": "Show a floating page-guide button",
  "help.drawer.floatingOn": "Shown in the bottom-right corner, where it can cover a table's scroll area and row buttons.",
  "help.drawer.floatingOff": "Off — the “?” in the top bar opens this guide, and stays clickable while a dialog is open.",
  "help.search.noResults": "No step matches.",
  "help.card.locate": "Show me where",
  "help.card.locateAria": "Locate: {{title}}",
  "help.card.tourFrom": "Start the tour here",
  "help.card.tourFromAria": "Start the tour at: {{title}}",
  "help.card.toSeeThis": "To see this:",
  "help.card.notOnScreen": "Not on screen right now.",
  "help.card.notOnScreenContext": "Not on screen right now. {{how}}",
  "help.card.notOnScreenOptional": "Not on screen right now — it only appears when there is data for it.",
  "help.tour.close": "End tour",
  "help.tour.back": "Back",
  "help.tour.next": "Next",
  "help.tour.done": "Done",
  "help.tour.prevSection": "Previous section",
  "help.tour.nextSection": "Next section",
  "help.tour.stepOf": "Step {{n}} of {{total}}",
  "help.tour.jumpAria": "Step {{n}} of {{total}} — jump to another step",
  "help.jump.search": "Search this page's guide…",
  "help.inspect.banner": "Click any field to read what it does",
  "help.inspect.escape": "Esc to leave",
  "help.inspect.leave": "Leave point-at-a-field mode",
  "help.inspect.miss": "Nothing documented here — try the control itself.",
  "help.inspect.continue": "Continue from here",
  "help.inspect.done": "Done",
  "help.ask.placeholder": "Ask how to do something…",
  "help.ask.hint": 'Plain words work best — "new CASCO offer", "mark invoice paid".',
  "help.ask.empty": "Ask a question about any screen in Quoting Gate.",
  "help.ask.noAnswer":
    'No answer for that yet. Try other words, or open "This page" for the guide of the page you are on.',
  "help.ask.needs": "Needs:",
  "help.ask.go": "Take me there",
  "help.ask.goOpen": "Take me there and open it",
  "help.ask.helped": "Answer helped",
  "help.ask.notHelped": "Answer did not help",
  "help.ask.notFound": "Opened the page, but the control is not on screen. {{needs}}",
  "help.ask.noOffer": "No offer on the list can do this right now. {{needs}}",
  "help.reveal.leaveNewOffer": "This is shown on another offer. Leave this new offer? What you entered here is not saved.",
  "help.untranslated": "Not translated yet — shown in English",
  "help.notice.nothingInDialog": "This dialog has no guide steps.",
  "help.notice.nothingOnScreen": "None of this page's guide steps is on screen right now.",
  "help.notice.stepHidden": "\"{{title}}\" is not on screen right now.",
  "help.notice.stepHiddenContext": "\"{{title}}\" is not on screen. {{how}}",
  "help.permission.canEdit": "Admins and internal users",
  "help.permission.canDelete": "Admins only",
  "help.permission.canViewSettings": "Admins and read-only admins",
  "help.permission.canEditSettings": "Admins only",
  "help.permission.canInvalidateOffers": "Admins and internal users",
  "help.permission.canMarkOffersAsError": "Admins only",
  "help.permission.canChooseOffersShowMode": "Admins and read-only admins",
};

const LV: Strings = {
  "help.launcher": "Palīdzība šai lapai",
  "help.drawer.title": "Lapas ceļvedis",
  "help.drawer.eyebrow": "Lapas ceļvedis",
  "help.drawer.close": "Aizvērt lapas ceļvedi",
  "help.drawer.language": "Ceļveža valoda",
  "help.drawer.tabPage": "Šī lapa",
  "help.drawer.tabAsk": "Jautāt",
  "help.drawer.startTour": "Ekskursija pa lapu",
  "help.drawer.inspect": "Norādīt lauku",
  "help.drawer.modesHint":
    "Ekskursija apmeklē to, kas pašlaik redzams ekrānā, vai tikai atvērto dialoglodziņu, ja tāds ir; tās soļu skaitītājs ļauj pāriet uz jebkuru soli. “Norādīt lauku” darbojas otrādi: noklikšķiniet uz vadīklas, kuru skatāties, un izlasiet, ko tā dara.",
  "help.drawer.searchAll": "Meklēt starp {{count}} ierakstiem…",
  "help.drawer.matches": "Atbilst {{count}} no {{total}} soļiem",
  "help.drawer.tips": "Noderīgi zināt",
  "help.drawer.floating": "Peldošā “?” poga",
  "help.drawer.floatingAria": "Rādīt peldošo lapas ceļveža pogu",
  "help.drawer.floatingOn": "Redzama apakšējā labajā stūrī, kur tā var aizsegt tabulas ritināšanas laukumu un rindu pogas.",
  "help.drawer.floatingOff": "Izslēgta — “?” augšējā joslā atver šo ceļvedi un paliek noklikšķināma arī tad, kad atvērts dialoglodziņš.",
  "help.search.noResults": "Neviens solis neatbilst.",
  "help.card.locate": "Parādīt, kur tas ir",
  "help.card.locateAria": "Atrast: {{title}}",
  "help.card.tourFrom": "Sākt ekskursiju no šejienes",
  "help.card.tourFromAria": "Sākt ekskursiju no: {{title}}",
  "help.card.toSeeThis": "Lai to redzētu:",
  "help.card.notOnScreen": "Pašlaik nav redzams ekrānā.",
  "help.card.notOnScreenContext": "Pašlaik nav redzams ekrānā. {{how}}",
  "help.card.notOnScreenOptional": "Pašlaik nav redzams ekrānā — tas parādās tikai tad, ja tam ir dati.",
  "help.tour.close": "Beigt ekskursiju",
  "help.tour.back": "Atpakaļ",
  "help.tour.next": "Tālāk",
  "help.tour.done": "Gatavs",
  "help.tour.prevSection": "Iepriekšējā sadaļa",
  "help.tour.nextSection": "Nākamā sadaļa",
  "help.tour.stepOf": "{{n}}. solis no {{total}}",
  "help.tour.jumpAria": "{{n}}. solis no {{total}} — pāriet uz citu soli",
  "help.jump.search": "Meklēt šīs lapas ceļvedī…",
  "help.inspect.banner": "Noklikšķiniet uz jebkura lauka, lai uzzinātu, ko tas dara",
  "help.inspect.escape": "Esc — iziet",
  "help.inspect.leave": "Iziet no lauka norādīšanas režīma",
  "help.inspect.miss": "Šeit nekas nav aprakstīts — mēģiniet uz pašas vadīklas.",
  "help.inspect.continue": "Turpināt no šejienes",
  "help.inspect.done": "Gatavs",
  "help.ask.placeholder": "Jautājiet, kā kaut ko izdarīt…",
  "help.ask.hint": "Vislabāk der vienkārši vārdi — “jauns KASKO piedāvājums”, “atzīmēt rēķinu kā apmaksātu”.",
  "help.ask.empty": "Uzdodiet jautājumu par jebkuru Quoting Gate ekrānu.",
  "help.ask.noAnswer":
    "Uz to vēl nav atbildes. Mēģiniet citus vārdus vai atveriet “Šī lapa”, lai redzētu pašreizējās lapas ceļvedi.",
  "help.ask.needs": "Nepieciešams:",
  "help.ask.go": "Aizvest mani turp",
  "help.ask.goOpen": "Aizvest turp un atvērt",
  "help.ask.helped": "Atbilde palīdzēja",
  "help.ask.notHelped": "Atbilde nepalīdzēja",
  "help.ask.notFound": "Lapa atvērta, bet vadīkla nav redzama ekrānā. {{needs}}",
  "help.ask.noOffer": "Sarakstā pašlaik nav neviena piedāvājuma, kuram to var izdarīt. {{needs}}",
  "help.reveal.leaveNewOffer": "Tas redzams citā piedāvājumā. Pamest šo jauno piedāvājumu? Šeit ievadītais netiks saglabāts.",
  "help.untranslated": "Vēl nav iztulkots — rādīts angliski",
  "help.notice.nothingInDialog": "Šim dialoglodziņam nav ceļveža soļu.",
  "help.notice.nothingOnScreen": "Pašlaik ekrānā nav neviena šīs lapas ceļveža soļa.",
  "help.notice.stepHidden": "“{{title}}” pašlaik nav redzams ekrānā.",
  "help.notice.stepHiddenContext": "“{{title}}” nav redzams ekrānā. {{how}}",
  "help.permission.canEdit": "Administratori un iekšējie lietotāji",
  "help.permission.canDelete": "Tikai administratori",
  "help.permission.canViewSettings": "Administratori un tikai lasīšanas administratori",
  "help.permission.canEditSettings": "Tikai administratori",
  "help.permission.canInvalidateOffers": "Administratori un iekšējie lietotāji",
  "help.permission.canMarkOffersAsError": "Tikai administratori",
  "help.permission.canChooseOffersShowMode": "Administratori un tikai lasīšanas administratori",
};

export const HELP_UI: Record<HelpLanguage, Strings> = { en: EN, lv: LV };

export type HelpText = (key: string, ...rest: (string | Record<string, unknown>)[]) => string;

/**
 * A `t`-shaped lookup bound to the guide language: `tx(key)`, `tx(key, vars)`
 * or `tx(key, defaultText, vars)` (a default is only used for an unknown key).
 */
export function helpText(language: HelpLanguage): HelpText {
  return (key, ...rest) => {
    const fallback = rest.find((r): r is string => typeof r === "string");
    const vars = rest.find((r): r is Record<string, unknown> => typeof r === "object") ?? {};
    const template = HELP_UI[language][key] ?? HELP_UI.en[key] ?? fallback ?? key;
    return template.replace(/\{\{(\w+)\}\}/g, (_, name: string) => String(vars[name] ?? ""));
  };
}
